import { CommonModule } from '@angular/common';
import { KuboAvatarComponent, KuboMood } from './kubo-avatar.component';
import { Component, EventEmitter, Input, Output } from '@angular/core';

export type UiStateTone = 'loading' | 'empty' | 'error' | 'success' | 'warning';

@Component({
  selector: 'app-ui-state',
  standalone: true,
  imports: [CommonModule, KuboAvatarComponent],
  template: `
    <!-- Con [kubo]: tarjeta con el asistente (estático: va dentro de contenido
         que se desplaza), como en los ejemplos de identidad de Kubo. -->
    <section *ngIf="kubo; else plainState" class="ui-state-kubo" [attr.role]="tone === 'error' ? 'alert' : 'status'">
      <app-kubo-avatar class="ui-state-kubo__avatar" [mood]="kubo" [shadow]="true"></app-kubo-avatar>
      <div class="ui-state-kubo__body">
        <h3>{{ title }}</h3>
        <p>{{ message }}</p>
        <button *ngIf="actionLabel" type="button" (click)="action.emit()">{{ actionLabel }} <i class="bi bi-arrow-right" aria-hidden="true"></i></button>
      </div>
    </section>
    <ng-template #plainState>
    <section class="ui-state" [class]="'ui-state ui-state--' + tone" [attr.role]="tone === 'error' ? 'alert' : 'status'">
      <span class="ui-state__icon" aria-hidden="true" *ngIf="tone !== 'empty'">
        <span *ngIf="tone === 'loading'" class="ui-state__spinner"></span>
        <i *ngIf="tone !== 'loading'" [class]="iconClass"></i>
      </span>
      <div class="ui-state__orb" aria-hidden="true" *ngIf="tone === 'empty'">
        <span class="ui-state__ring"></span>
        <span class="ui-state__ring ui-state__ring--outer"></span>
        <span class="ui-state__mote m1"></span>
        <span class="ui-state__mote m2"></span>
        <span class="ui-state__mote m3"></span>
        <span class="ui-state__glass"><i [class]="iconClass"></i></span>
      </div>
      <div>
        <h3>{{ title }}</h3>
        <p>{{ message }}</p>
      </div>
      <button *ngIf="actionLabel" type="button" (click)="action.emit()">{{ actionLabel }}</button>
    </section>
    </ng-template>
  `,
  styles: [`
    :host { display:block }
    .ui-state-kubo{display:flex;align-items:center;justify-content:center;gap:1.4rem;padding:1.6rem 1.4rem;border:1px solid var(--ts-border-strong);border-radius:var(--ts-radius-lg,18px);background:linear-gradient(135deg,color-mix(in srgb,var(--ts-primary) 9%,var(--ts-surface)),var(--ts-surface));text-align:left}
    .ui-state-kubo__avatar{width:112px;height:112px}
    .ui-state-kubo__body{display:grid;justify-items:start;gap:.35rem;max-width:42ch}
    .ui-state-kubo h3{margin:0;color:var(--ts-text);font-size:1rem;font-weight:800}
    .ui-state-kubo p{margin:0;color:var(--ts-text-muted);font-size:.8rem;line-height:1.55}
    .ui-state-kubo button{display:inline-flex;align-items:center;gap:.45rem;min-height:40px;margin-top:.55rem;padding:0 1.15rem;color:var(--ts-on-primary,#fff);border:0;border-radius:999px;background:linear-gradient(135deg,var(--ts-primary),var(--ts-primary-strong));box-shadow:0 10px 22px color-mix(in srgb,var(--ts-primary) 32%,transparent);font:inherit;font-size:.8rem;font-weight:800;cursor:pointer}
    .ui-state-kubo button:hover{filter:brightness(1.06)}
    @media(max-width:560px){.ui-state-kubo{flex-direction:column;text-align:center}.ui-state-kubo__body{justify-items:center}.ui-state-kubo__avatar{width:96px;height:96px}}
    .ui-state{display:grid;justify-items:center;gap:.8rem;padding:2.5rem 1rem;text-align:center;color:var(--ts-text-muted);border:1px dashed var(--ts-border-strong);border-radius:var(--ts-radius-md);background:var(--ts-surface-soft)}
    .ui-state__icon{display:grid;place-items:center;width:46px;height:46px;color:var(--ts-text-blue-250-36);border-radius:13px;background:var(--ts-primary-soft-07);font-size:1.15rem}
    h3{margin:0;color:var(--ts-text);font-size:.92rem}p{max-width:48ch;margin:.3rem 0 0;color:var(--ts-text-muted);font-size:.75rem;line-height:1.55}
    button{min-height:36px;padding:0 .75rem;color:var(--ts-on-primary);border:1px solid var(--ts-primary-border);border-radius:var(--ts-radius-sm);background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo));font-size:.72rem;font-weight:700;cursor:pointer}
    .ui-state--error .ui-state__icon{color:var(--ts-text-red-200-5);background:var(--ts-bg-red-350-a10)}
    .ui-state--warning .ui-state__icon{color:var(--ts-warning-text);background:var(--ts-bg-orange-450-a10-2)}
    .ui-state--success .ui-state__icon{color:var(--ts-success-text);background:var(--ts-bg-green-500-a10)}
    .ui-state__spinner{width:22px;height:22px;border:2px solid var(--ts-border-blue-350-a22-2);border-top-color:var(--ts-primary);border-radius:50%;animation:spin .8s linear infinite}
    @keyframes spin{to{transform:rotate(360deg)}}

    /* Estado "vacío": ícono con anillos suaves y motas, ESTÁTICO: va dentro
       de contenido que se desplaza y una animación infinita con transform lo
       dejaba en una capa aparte que "flotaba" al hacer scroll. Con [kubo] se
       muestra al asistente en su lugar. */
    .ui-state__orb{position:relative;width:70px;height:70px;margin-bottom:.2rem;display:grid;place-items:center}
    .ui-state__glass{position:relative;z-index:2;display:grid;place-items:center;width:48px;height:48px;border-radius:50%;color:var(--ts-text-blue-250-36);border:1.5px solid var(--ts-border-blue-250-a40-5);background:var(--ts-bg-blue-400-a10);font-size:1.05rem;box-shadow:0 8px 20px var(--ts-shadow-gray-1000-a22)}
    .ui-state__ring{position:absolute;inset:3px;border-radius:50%;border:1px solid var(--ts-border-blue-250-a40-5);opacity:.55}
    .ui-state__ring--outer{inset:-7px;border-style:dashed;opacity:.35}
    .ui-state__mote{position:absolute;border-radius:50%;background:var(--ts-bg-blue-250-2);opacity:.5}
    .ui-state__mote.m1{width:5px;height:5px;top:-2px;right:0;animation-delay:0s}
    .ui-state__mote.m2{width:4px;height:4px;bottom:6px;left:-9px;animation-delay:1s}
    .ui-state__mote.m3{width:3px;height:3px;top:22px;right:-10px;animation-delay:2s}
    @keyframes uiStateFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
    @keyframes uiStateRing{0%,100%{transform:scale(.94);opacity:.35}50%{transform:scale(1.06);opacity:.7}}
    @keyframes uiStateMote{0%,100%{opacity:0;transform:translateY(0) scale(.6)}50%{opacity:.85;transform:translateY(-6px) scale(1)}}
    @media(prefers-reduced-motion:reduce){
      .ui-state__orb,.ui-state__ring,.ui-state__ring--outer,.ui-state__mote,.ui-state__spinner{animation:none !important}
      .ui-state__mote{opacity:.5}
    }
  `]
})
export class UiStateComponent {
  @Input() tone: UiStateTone = 'empty';
  @Input() title = 'Sin información';
  @Input() message = '';
  @Input() actionLabel = '';
  @Input() icon = '';
  /** Muestra a Kubo con esta expresión en lugar del ícono genérico. */
  @Input() kubo: KuboMood | null = null;
  @Output() action = new EventEmitter<void>();

  get iconClass(): string {
    if (this.icon) return this.icon;
    return {
      empty: 'bi bi-search',
      error: 'bi bi-exclamation-triangle',
      success: 'bi bi-check-circle',
      warning: 'bi bi-exclamation-circle',
      loading: ''
    }[this.tone];
  }
}
